import { getDocumentDisplayName } from '@app/features/onboarding/v2/utils/document-display-name';
import { ClassifiedDocumentUploadResponse } from '@interfaces/accounting-document-management/responses/classified-document-upload.response';

/**
 * Monta a frase que o usuário lê depois de enviar um documento por esta tela.
 *
 * **Por que a frase é montada aqui e não no backend.** O documento passou a casar com um item do
 * *catálogo de documentos esperados do onboarding*, e as chaves desse catálogo são identificadores
 * PascalCase em inglês (`BalanceSheetIncomeStatementCashFlow`, `AnnualBilling`, ...). O backend só conhece
 * a chave; o rótulo que o usuário reconhece ("Balanço, DRE e fluxo de caixa") mora no frontend, em
 * `document-display-name.ts`, junto das demais traduções do onboarding. Enquanto a frase vinha pronta do
 * backend, ela exibia a chave crua — a tela chegou a anunciar *"classificou como
 * BalanceSheetIncomeStatementCashFlow"* (achado da validação de ponta a ponta de 25/08/2026).
 *
 * Importar o utilitário do onboarding é deliberado: o catálogo **é** o do onboarding, e duplicar o mapa
 * aqui garantiria que as duas telas divergissem no nome do mesmo documento — exatamente o que este PBI
 * veio eliminar.
 */
export function buildClassificationMessage(
  result: ClassifiedDocumentUploadResponse,
  fileName: string
): string {
  const described = [...result.periods]
    .sort((a, b) => a.year - b.year)
    .map(period => `${describeDocument(period.matchedDocumentName)} · ${describePeriod(period)}`);

  const base = described.length
    ? `Cog AI leu ${fileName} e classificou como ${described.join('; ')}`
    : // Sem período nenhum a tela nem chega aqui (cai no aviso de revisão humana), mas um fallback honesto
      // evita frase truncada se o contrato mudar.
      `Cog AI leu ${fileName}.`;

  return result.awaitingApproval ? `${base} Aguardando aprovação.` : base;
}

/**
 * Rótulo do item do catálogo. Sem a chave — contrato antigo ou item novo ainda sem tradução —, descreve
 * genericamente em vez de exibir identificador interno: "documento contábil" é vago, mas é português.
 */
function describeDocument(matchedDocumentName: string | null | undefined): string {
  if (!matchedDocumentName) return 'documento contábil';

  const label = getDocumentDisplayName(matchedDocumentName);
  return label && label !== matchedDocumentName ? label : 'documento contábil';
}

/** "anual de 2023", "jan–jun de 2025", "abr de 2024" — a mesma leitura que a régua usa nos períodos. */
function describePeriod(period: { year: number; startMonth: number; endMonth: number }): string {
  const { year, startMonth, endMonth } = period;

  if (startMonth === 1 && endMonth === 12) return `anual de ${year}`;
  if (startMonth === endMonth) return `${MONTH_LABELS[startMonth - 1]} de ${year}`;

  return `${MONTH_LABELS[startMonth - 1]}–${MONTH_LABELS[endMonth - 1]} de ${year}`;
}

const MONTH_LABELS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];
